import * as faceapi from '@vladmandic/face-api';
import api from './api';

let modelsLoaded = false;

export async function loadFaceDetectionModels() {
  if (modelsLoaded) return;

  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri('/api/models'),
    faceapi.nets.faceLandmark68Net.loadFromUri('/api/models'),
    faceapi.nets.faceRecognitionNet.loadFromUri('/api/models'),
    faceapi.nets.faceExpressionNet.loadFromUri('/api/models')
  ]);

  modelsLoaded = true;
}

export async function detectFace(imageElement: HTMLImageElement) {
  await loadFaceDetectionModels();

  const detection = await faceapi
    .detectSingleFace(imageElement, new faceapi.TinyFaceDetectorOptions())
    .withFaceLandmarks()
    .withFaceDescriptor();

  if (!detection) {
    throw new Error('No face detected in the image');
  }

  return detection;
}

/**
 * ENHANCED LIVENESS DETECTION
 *
 * This implementation includes:
 * - Blink detection using Eye Aspect Ratio (EAR)
 * - Challenge-response (smile, head turn)
 * - Texture analysis (detect photos vs real faces)
 * - Head pose stability checks
 * - Anti-replay detection
 *
 * Limitations (requires commercial API for better):
 * - Can be bypassed with sophisticated video replay
 * - No 3D depth detection (would require TrueDepth camera)
 * - No infrared liveness (would require hardware)
 */

// Eye landmark indices for 68-point model
const LEFT_EYE_INDICES = [36, 37, 38, 39, 40, 41];
const RIGHT_EYE_INDICES = [42, 43, 44, 45, 46, 47];
const MOUTH_INDICES = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59];

export interface LivenessResult {
  isLive: boolean;
  score: number;
  checks: {
    blinkDetected: boolean;
    naturalMovement: boolean;
    expressionVariation: boolean;
    textureAnalysis: boolean;
    headPoseValid: boolean;
  };
  failureReasons: string[];
}

export interface LivenessChallenge {
  type: 'blink' | 'smile' | 'turn_left' | 'turn_right' | 'nod';
  instruction: string;
  instructionFil: string; // Filipino translation
  timeout: number; // milliseconds
}

// Available challenges for liveness verification
export const LIVENESS_CHALLENGES: LivenessChallenge[] = [
  {
    type: 'blink',
    instruction: 'Please blink your eyes',
    instructionFil: 'Pakikurap ang iyong mga mata',
    timeout: 5000,
  },
  {
    type: 'smile',
    instruction: 'Please smile',
    instructionFil: 'Pakingiti',
    timeout: 5000,
  },
  {
    type: 'turn_left',
    instruction: 'Please turn your head slightly to the left',
    instructionFil: 'Pakiharap ng kaunti sa kaliwa',
    timeout: 5000,
  },
  {
    type: 'turn_right',
    instruction: 'Please turn your head slightly to the right',
    instructionFil: 'Pakiharap ng kaunti sa kanan',
    timeout: 5000,
  },
];

/**
 * Calculate Eye Aspect Ratio (EAR) for blink detection
 * EAR = (||p2-p6|| + ||p3-p5||) / (2 * ||p1-p4||)
 */
function calculateEAR(landmarks: faceapi.Point[], eyeIndices: number[]): number {
  const p1 = landmarks[eyeIndices[0]];
  const p2 = landmarks[eyeIndices[1]];
  const p3 = landmarks[eyeIndices[2]];
  const p4 = landmarks[eyeIndices[3]];
  const p5 = landmarks[eyeIndices[4]];
  const p6 = landmarks[eyeIndices[5]];

  const vertical1 = Math.sqrt(Math.pow(p2.x - p6.x, 2) + Math.pow(p2.y - p6.y, 2));
  const vertical2 = Math.sqrt(Math.pow(p3.x - p5.x, 2) + Math.pow(p3.y - p5.y, 2));
  const horizontal = Math.sqrt(Math.pow(p1.x - p4.x, 2) + Math.pow(p1.y - p4.y, 2));

  return (vertical1 + vertical2) / (2 * horizontal);
}

/**
 * Detect blink by monitoring EAR over time
 */
async function detectBlink(
  videoElement: HTMLVideoElement,
  timeout: number = 5000
): Promise<boolean> {
  const startTime = Date.now();
  const EAR_THRESHOLD = 0.2; // Below this = eye closed
  const BLINK_FRAMES = 3; // Must be below threshold for this many consecutive frames

  let consecutiveClosedFrames = 0;
  let blinkDetected = false;
  let wasOpen = true;

  while (Date.now() - startTime < timeout && !blinkDetected) {
    const detection = await faceapi
      .detectSingleFace(videoElement, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks();

    if (detection) {
      const landmarks = detection.landmarks.positions;
      const leftEAR = calculateEAR(landmarks, LEFT_EYE_INDICES);
      const rightEAR = calculateEAR(landmarks, RIGHT_EYE_INDICES);
      const avgEAR = (leftEAR + rightEAR) / 2;

      if (avgEAR < EAR_THRESHOLD) {
        consecutiveClosedFrames++;
        if (consecutiveClosedFrames >= BLINK_FRAMES && wasOpen) {
          // Eyes were open, now closed for enough frames = blink started
          wasOpen = false;
        }
      } else {
        if (!wasOpen && consecutiveClosedFrames >= BLINK_FRAMES) {
          // Eyes were closed, now open = blink completed
          blinkDetected = true;
        }
        consecutiveClosedFrames = 0;
        wasOpen = true;
      }
    }

    await new Promise(resolve => setTimeout(resolve, 100));
  }

  return blinkDetected;
}

/**
 * Detect smile using mouth aspect ratio and expression scores
 */
async function detectSmile(
  videoElement: HTMLVideoElement,
  timeout: number = 5000
): Promise<boolean> {
  const startTime = Date.now();
  const SMILE_THRESHOLD = 0.5; // Expression score threshold

  while (Date.now() - startTime < timeout) {
    const detection = await faceapi
      .detectSingleFace(videoElement, new faceapi.TinyFaceDetectorOptions())
      .withFaceExpressions();

    if (detection && detection.expressions.happy > SMILE_THRESHOLD) {
      return true;
    }

    await new Promise(resolve => setTimeout(resolve, 100));
  }

  return false;
}

/**
 * Detect head turn by monitoring nose position relative to face center
 */
async function detectHeadTurn(
  videoElement: HTMLVideoElement,
  direction: 'left' | 'right',
  timeout: number = 5000
): Promise<boolean> {
  const startTime = Date.now();
  const initialDetection = await faceapi
    .detectSingleFace(videoElement, new faceapi.TinyFaceDetectorOptions())
    .withFaceLandmarks();

  if (!initialDetection) return false;

  const initialNose = initialDetection.landmarks.getNose()[0];
  const initialBox = initialDetection.detection.box;
  const initialCenterX = initialBox.x + initialBox.width / 2;
  const initialOffset = initialNose.x - initialCenterX;

  const TURN_THRESHOLD = initialBox.width * 0.1; // 10% of face width

  while (Date.now() - startTime < timeout) {
    const detection = await faceapi
      .detectSingleFace(videoElement, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks();

    if (detection) {
      const nose = detection.landmarks.getNose()[0];
      const box = detection.detection.box;
      const centerX = box.x + box.width / 2;
      const offset = nose.x - centerX;
      const turnAmount = offset - initialOffset;

      if (direction === 'left' && turnAmount < -TURN_THRESHOLD) {
        return true;
      }
      if (direction === 'right' && turnAmount > TURN_THRESHOLD) {
        return true;
      }
    }

    await new Promise(resolve => setTimeout(resolve, 100));
  }

  return false;
}

/**
 * Analyze texture to detect photos vs real faces
 * Photos typically have less texture variation and different noise patterns
 */
async function analyzeTexture(videoElement: HTMLVideoElement): Promise<{
  isRealFace: boolean;
  score: number;
}> {
  // Get canvas from video
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { isRealFace: false, score: 0 };

  ctx.drawImage(videoElement, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data, width, height } = imageData;

  // Calculate local binary pattern variance (simplified)
  let textureScore = 0;
  const sampleSize = Math.min(100, Math.min(width, height) / 4);

  // Sample the face region (center of image)
  const startX = Math.floor(width / 2 - sampleSize / 2);
  const startY = Math.floor(height / 2 - sampleSize / 2);

  for (let y = startY + 1; y < startY + sampleSize - 1; y++) {
    for (let x = startX + 1; x < startX + sampleSize - 1; x++) {
      const idx = (y * width + x) * 4;
      const center = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;

      // Compare with 8 neighbors
      const neighbors = [
        ((y - 1) * width + x - 1) * 4,
        ((y - 1) * width + x) * 4,
        ((y - 1) * width + x + 1) * 4,
        (y * width + x - 1) * 4,
        (y * width + x + 1) * 4,
        ((y + 1) * width + x - 1) * 4,
        ((y + 1) * width + x) * 4,
        ((y + 1) * width + x + 1) * 4,
      ];

      let localVariance = 0;
      for (const nIdx of neighbors) {
        const neighbor = (data[nIdx] + data[nIdx + 1] + data[nIdx + 2]) / 3;
        localVariance += Math.abs(center - neighbor);
      }

      textureScore += localVariance / 8;
    }
  }

  const avgTexture = textureScore / Math.pow(sampleSize - 2, 2);

  // Real faces typically have texture score between 5-30
  // Photos of photos often have lower variance or very high (noise from screen)
  const isRealFace = avgTexture > 5 && avgTexture < 40;
  const score = Math.min(100, Math.max(0, (avgTexture - 5) * 3));

  return { isRealFace, score };
}

/**
 * Enhanced liveness check with multiple verification methods
 */
export async function performLivenessCheck(videoElement: HTMLVideoElement): Promise<boolean> {
  await loadFaceDetectionModels();

  const result = await performEnhancedLivenessCheck(videoElement);
  return result.isLive;
}

/**
 * Comprehensive liveness check with detailed results
 */
export async function performEnhancedLivenessCheck(
  videoElement: HTMLVideoElement,
  options: {
    requireBlink?: boolean;
    requireSmile?: boolean;
    requireHeadTurn?: boolean;
    checkTexture?: boolean;
  } = {}
): Promise<LivenessResult> {
  await loadFaceDetectionModels();

  const {
    requireBlink = true,
    requireSmile = false,
    requireHeadTurn = false,
    checkTexture = true,
  } = options;

  const failureReasons: string[] = [];
  const checks = {
    blinkDetected: false,
    naturalMovement: false,
    expressionVariation: false,
    textureAnalysis: false,
    headPoseValid: false,
  };

  // Collect baseline data
  const baselineChecks = [];
  for (let i = 0; i < 10; i++) {
    const detection = await faceapi
      .detectSingleFace(videoElement, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceExpressions();

    if (!detection) {
      failureReasons.push('Face not consistently detected');
      return {
        isLive: false,
        score: 0,
        checks,
        failureReasons,
      };
    }

    baselineChecks.push({
      expressions: detection.expressions,
      landmarks: detection.landmarks.positions,
      box: detection.detection.box,
    });

    await new Promise(resolve => setTimeout(resolve, 200));
  }

  // Check for natural movement
  checks.naturalMovement = analyzeLandmarkMovement(baselineChecks);
  if (!checks.naturalMovement) {
    failureReasons.push('Insufficient natural movement detected');
  }

  // Check for expression variation
  checks.expressionVariation = analyzeExpressionVariation(baselineChecks);
  if (!checks.expressionVariation) {
    failureReasons.push('Insufficient expression variation');
  }

  // Check head pose stability (should be relatively stable but not perfectly still)
  const headPoseStability = analyzeHeadPoseStability(baselineChecks);
  checks.headPoseValid = headPoseStability > 0.3 && headPoseStability < 0.9;
  if (!checks.headPoseValid) {
    failureReasons.push('Abnormal head pose stability');
  }

  // Blink detection
  if (requireBlink) {
    checks.blinkDetected = await detectBlink(videoElement, 5000);
    if (!checks.blinkDetected) {
      failureReasons.push('No blink detected');
    }
  } else {
    checks.blinkDetected = true;
  }

  // Smile detection (optional challenge)
  if (requireSmile) {
    const smileDetected = await detectSmile(videoElement, 5000);
    if (!smileDetected) {
      failureReasons.push('Smile challenge not completed');
    }
  }

  // Head turn detection (optional challenge)
  if (requireHeadTurn) {
    const turnDetected = await detectHeadTurn(videoElement, 'left', 5000);
    if (!turnDetected) {
      failureReasons.push('Head turn challenge not completed');
    }
  }

  // Texture analysis
  if (checkTexture) {
    const textureResult = await analyzeTexture(videoElement);
    checks.textureAnalysis = textureResult.isRealFace;
    if (!checks.textureAnalysis) {
      failureReasons.push('Texture analysis suggests possible photo/screen');
    }
  } else {
    checks.textureAnalysis = true;
  }

  // Calculate overall score
  const passedChecks = Object.values(checks).filter(Boolean).length;
  const totalChecks = Object.keys(checks).length;
  const score = Math.round((passedChecks / totalChecks) * 100);

  // Must pass at least 4 out of 5 checks
  const isLive = passedChecks >= 4;

  return {
    isLive,
    score,
    checks,
    failureReasons,
  };
}

/**
 * Perform challenge-response liveness verification
 */
export async function performChallengeResponse(
  videoElement: HTMLVideoElement,
  challenge: LivenessChallenge
): Promise<boolean> {
  await loadFaceDetectionModels();

  switch (challenge.type) {
    case 'blink':
      return detectBlink(videoElement, challenge.timeout);
    case 'smile':
      return detectSmile(videoElement, challenge.timeout);
    case 'turn_left':
      return detectHeadTurn(videoElement, 'left', challenge.timeout);
    case 'turn_right':
      return detectHeadTurn(videoElement, 'right', challenge.timeout);
    default:
      return false;
  }
}

/**
 * Get random challenges for liveness verification
 */
export function getRandomChallenges(count: number = 2): LivenessChallenge[] {
  const shuffled = [...LIVENESS_CHALLENGES].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * Analyze head pose stability over time
 * Returns value between 0 (perfectly still) and 1 (very unstable)
 */
function analyzeHeadPoseStability(checks: any[]): number {
  if (checks.length < 2) return 0;

  let totalVariance = 0;

  for (let i = 1; i < checks.length; i++) {
    const prevBox = checks[i - 1].box;
    const currBox = checks[i].box;

    // Calculate movement of face bounding box
    const dx = Math.abs(currBox.x - prevBox.x) / currBox.width;
    const dy = Math.abs(currBox.y - prevBox.y) / currBox.height;
    const dw = Math.abs(currBox.width - prevBox.width) / currBox.width;
    const dh = Math.abs(currBox.height - prevBox.height) / currBox.height;

    totalVariance += dx + dy + dw + dh;
  }

  const avgVariance = totalVariance / (checks.length - 1);

  // Normalize to 0-1 range
  return Math.min(1, avgVariance * 10);
}

function analyzeExpressionVariation(checks: any[]): boolean {
  // Check for natural variations in expressions
  const expressionVariations = checks.map(check =>
    Object.values(check.expressions)
  );

  // Calculate standard deviation of expressions
  const stdDev = calculateStandardDeviation(expressionVariations.flat() as number[]);

  // Natural expressions should show some variation
  return stdDev > 0.1;
}

function analyzeLandmarkMovement(checks: any[]): boolean {
  // Check for subtle natural head movements
  const movements = checks.map(check =>
    check.landmarks.map((point: any) => [point.x, point.y])
  );

  // Calculate average movement between frames
  let totalMovement = 0;
  for (let i = 1; i < movements.length; i++) {
    totalMovement += calculateAverageMovement(
      movements[i],
      movements[i - 1]
    );
  }

  const avgMovement = totalMovement / (movements.length - 1);

  // Movement should be present but not too extreme
  return avgMovement > 0.5 && avgMovement < 10;
}

function calculateStandardDeviation(values: number[]): number {
  const avg = values.reduce((a, b) => a + b) / values.length;
  const squareDiffs = values.map(value => Math.pow(value - avg, 2));
  const avgSquareDiff = squareDiffs.reduce((a, b) => a + b) / squareDiffs.length;
  return Math.sqrt(avgSquareDiff);
}

function calculateAverageMovement(points1: number[][], points2: number[][]): number {
  let totalDistance = 0;
  for (let i = 0; i < points1.length; i++) {
    const dx = points1[i][0] - points2[i][0];
    const dy = points1[i][1] - points2[i][1];
    totalDistance += Math.sqrt(dx * dx + dy * dy);
  }
  return totalDistance / points1.length;
}

export async function compareFaces(idImage: HTMLImageElement, selfieImage: HTMLImageElement) {
  await loadFaceDetectionModels();

  const idDetection = await detectFace(idImage);
  const selfieDetection = await detectFace(selfieImage);

  if (!idDetection || !selfieDetection) {
    throw new Error('Could not detect faces in both images');
  }

  const distance = faceapi.euclideanDistance(
    idDetection.descriptor,
    selfieDetection.descriptor
  );

  // Threshold for face matching (lower means stricter matching)
  // Increased to 0.6 to avoid false negatives in client-side check
  const MATCH_THRESHOLD = 0.6;
  return distance < MATCH_THRESHOLD;
}

export async function storeVerificationData(userId: string, verificationData: {
  idDocument: File;
  selfie: File;
  faceMatchScore: number;
  livenessScore: number;
}) {
  const response = await api.request('/api/verifications/biometric', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      face_match_score: verificationData.faceMatchScore,
      liveness_score: verificationData.livenessScore,
      status: 'pending_review',
    }),
  });

  if (!response.success) {
    throw new Error(response.error || 'Failed to store verification data');
  }

  return response.data;
}
